import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';

import { HoldReceiptInfo } from '../../models/hold-receipt.model';

/**
 * ResumeReceiptDialogComponent
 *
 * Standalone child component responsible for:
 *  - Rendering the Resume Receipt modal with the list of held orders
 *  - Showing an empty-state view when no orders are on hold
 *  - Auto-focusing the first Resume button when the dialog opens
 *  - Trapping Tab keyboard focus within the dialog
 *  - Emitting the selected receipt UUID to the parent (Dashboard)
 *
 * The component owns ZERO business logic — it does NOT call APIs directly.
 * The Dashboard calls getSpecificTransaction() on resume.
 *
 * Replaces the inline <p-dialog> block at L1482–1601 of
 * dashboard.component.html.
 */
@Component({
  selector: 'app-resume-receipt-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, TableModule],
  templateUrl: './resume-receipt-dialog.component.html',
  styleUrl: './resume-receipt-dialog.component.scss',
})
export class ResumeReceiptDialogComponent implements OnChanges, AfterViewInit {
  // ── Inputs ───────────────────────────────────────────────────────────────

  /** Controls dialog visibility (two-way bindable). */
  @Input() visible: boolean = false;

  /** The list of held receipts fetched by the parent (Dashboard / facade). */
  @Input() savedReceipts: HoldReceiptInfo[] = [];

  // ── Outputs ──────────────────────────────────────────────────────────────

  /** Two-way binding support for [(visible)]. */
  @Output() visibleChange = new EventEmitter<boolean>();

  /**
   * Emitted when the cashier clicks Resume on a receipt row.
   * Carries the hold-order UUID — the Dashboard calls getSpecificTransaction().
   */
  @Output() resumed = new EventEmitter<string>();

  /** Emitted when the cashier closes the dialog without selecting a receipt. */
  @Output() closed = new EventEmitter<void>();

  // ── ViewChildren ─────────────────────────────────────────────────────────

  /** All "Resume" buttons in the table — used for keyboard navigation. */
  @ViewChildren('resumeBtn') resumeButtonRefs!: QueryList<ElementRef<HTMLButtonElement>>;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      setTimeout(() => this.focusFirstResumeButton(), 150);
    }
  }

  ngAfterViewInit(): void {
    if (this.visible) {
      setTimeout(() => this.focusFirstResumeButton(), 150);
    }
  }

  // ── Public actions ────────────────────────────────────────────────────────

  onResume(uuid: string): void {
    this.resumed.emit(uuid);
  }

  onClose(): void {
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  /**
   * Tab key trap inside the dialog — mirrors handleResumeReceiptDialogTabNavigation()
   * from dashboard.component.ts (L9153–9254).
   *
   * Navigation order: Close button ↔ Resume buttons (row by row).
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;

    event.preventDefault();
    event.stopPropagation();

    const dialog = (event.currentTarget as HTMLElement).closest(
      '.resume-dialog-container'
    ) as HTMLElement | null;
    if (!dialog) return;

    const closeBtn = dialog.querySelector('.premium-close-btn') as HTMLElement | null;
    const resumeButtons = this.resumeButtonRefs
      ? (Array.from(this.resumeButtonRefs).map((r) => r.nativeElement) as HTMLElement[])
      : [];

    const activeElement = document.activeElement as HTMLElement;

    // If no resume buttons, cycle only the close button
    if (resumeButtons.length === 0) {
      closeBtn?.focus();
      return;
    }

    const firstResumeBtn = resumeButtons[0];

    // Close → first Resume
    if (!event.shiftKey && activeElement === closeBtn) {
      firstResumeBtn.focus();
      return;
    }

    // First Resume → Close (Shift+Tab)
    if (event.shiftKey && activeElement === firstResumeBtn) {
      closeBtn?.focus();
      return;
    }

    // Navigate between Resume buttons
    const currentIndex = resumeButtons.indexOf(activeElement);
    if (currentIndex !== -1) {
      if (!event.shiftKey) {
        const next = resumeButtons[currentIndex + 1];
        if (next) {
          next.focus();
        } else {
          closeBtn?.focus();
        }
      } else {
        const prev = resumeButtons[currentIndex - 1];
        if (prev) {
          prev.focus();
        } else {
          closeBtn?.focus();
        }
      }
      return;
    }

    // Fallback
    if (!event.shiftKey) {
      firstResumeBtn.focus();
    } else {
      closeBtn?.focus();
    }
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  private focusFirstResumeButton(): void {
    const buttons = this.resumeButtonRefs?.toArray();
    if (buttons && buttons.length > 0) {
      buttons[0].nativeElement.focus();
    } else {
      // No receipts — focus close button
      const dialog = document.querySelector('.resume-dialog-container') as HTMLElement | null;
      const closeBtn = dialog?.querySelector('.premium-close-btn') as HTMLButtonElement | null;
      closeBtn?.focus();
    }
  }
}
